import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('username, role, is_admin')
          .eq('id', session.user.id)
          .single();

        setUser({
          id: session.user.id,
          email: session.user.email,
          username: profile?.username,
          role: profile?.role,
          isAdmin: profile?.is_admin,
        });
      }
    };

    getSession();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        supabase
          .from('profiles')
          .select('username, role, is_admin')
          .eq('id', session.user.id)
          .single()
          .then(({ data: profile }) => {
            setUser({
              id: session.user.id,
              email: session.user.email,
              username: profile?.username,
              role: profile?.role,
              isAdmin: profile?.is_admin,
            });
          });
      } else {
        setUser(null);
      }
    });

    return () => listener?.subscription?.unsubscribe();
  }, []);

  const checkUsernameAvailable = async (username) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .eq('username', username)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return !data; // true if username is available
  };

  const signup = async (email, password, username, role = 'student') => {
    const isAvailable = await checkUsernameAvailable(username);
    if (!isAvailable) {
      throw new Error('Username is already taken.');
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) throw error;

    const user = data.user;
    if (user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .insert([{ id: user.id, username, role, is_admin: false }]);
      if (profileError) throw profileError;

      setUser({ id: user.id, email, username, role, isAdmin: false });
    }

    return data;
  };

  const login = async (emailOrUsername, password) => {
    // Try logging in using username
    let email = emailOrUsername;

    if (!email.includes('@')) {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', emailOrUsername)
        .single();

      if (error || !profile?.id) {
        throw new Error('Invalid username or email.');
      }

      const { data: userData, error: userError } = await supabase
        .from('auth.users')
        .select('email')
        .eq('id', profile.id)
        .single();

      if (userError || !userData?.email) {
        throw new Error('User not found.');
      }

      email = userData.email;
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    const user = data.user;
    const { data: profile } = await supabase
      .from('profiles')
      .select('username, role, is_admin')
      .eq('id', user.id)
      .single();

    setUser({
      id: user.id,
      email: user.email,
      username: profile?.username,
      role: profile?.role,
      isAdmin: profile?.is_admin,
    });

    return data;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  const loginWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
    });
    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        signup,
        login,
        logout,
        checkUsernameAvailable,
        loginWithGoogle,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
